import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../../context/AdminContext';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Shield, AlertTriangle, CheckCircle, XCircle, 
  Clock, Mail, Ban, Eye 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminFraud = () => {
  const { getAuthHeaders } = useAdmin();
  const [alerts, setAlerts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [flaggedEmails, setFlaggedEmails] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('alerts');
  
  // Review form
  const [reviewingId, setReviewingId] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [alertsRes, logsRes, emailsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/admin/fraud/alerts`, { headers }),
        axios.get(`${API}/admin/fraud/logs`, { headers }),
        axios.get(`${API}/admin/fraud/flagged-emails`, { headers }),
        axios.get(`${API}/admin/fraud/analytics`, { headers })
      ]);
      
      setAlerts(alertsRes.data.alerts || []);
      setLogs(logsRes.data.logs || []);
      setFlaggedEmails(emailsRes.data.flagged_emails || []);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching fraud data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (logId, approved) => {
    setSubmitting(true);
    try {
      await axios.put(`${API}/admin/fraud/review/${logId}`, {
        approved,
        notes: reviewNotes
      }, { headers: getAuthHeaders() });
      
      setReviewingId(null);
      setReviewNotes('');
      fetchData();
    } catch (error) {
      console.error('Error reviewing:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnflagEmail = async (email) => {
    try {
      await axios.delete(`${API}/admin/fraud/unflag-email/${email}`, {
        headers: getAuthHeaders()
      });
      fetchData();
    } catch (error) {
      console.error('Error unflagging email:', error);
    }
  };

  const getRiskColor = (level) => {
    const colors = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800'
    };
    return colors[level] || 'bg-stone-100 text-stone-800';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 space-y-6" data-testid="admin-fraud">
        <h1 className="text-2xl font-bold text-stone-800">Fraud Detection</h1>

        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Shield className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-800">{analytics?.total_checks || 0}</p>
                  <p className="text-stone-500 text-sm">Total Checks</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-100 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-800">{analytics?.manual_reviews_required || 0}</p>
                  <p className="text-stone-500 text-sm">Pending Reviews</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-800">{analytics?.reviews_completed || 0}</p>
                  <p className="text-stone-500 text-sm">Reviews Done</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <Ban className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-800">{analytics?.blocked_orders || 0}</p>
                  <p className="text-stone-500 text-sm">Blocked Orders</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="alerts">
              Alerts ({alerts.length})
            </TabsTrigger>
            <TabsTrigger value="logs">
              All Logs ({logs.length})
            </TabsTrigger>
            <TabsTrigger value="flagged">
              Flagged Emails ({flaggedEmails.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="alerts" className="space-y-4">
            {alerts.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Shield className="h-16 w-16 mx-auto mb-4 text-green-300" />
                  <h3 className="text-lg font-medium text-stone-800 mb-2">No alerts</h3>
                  <p className="text-stone-500">All orders are passing fraud checks</p>
                </CardContent>
              </Card>
            ) : (
              alerts.map((alert) => (
                <Card key={alert.id} className="border-l-4 border-l-yellow-500">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="font-semibold text-stone-800">{alert.email}</p>
                        <p className="text-sm text-stone-500">Order: {alert.order_id}</p>
                        <p className="text-sm text-stone-500 flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {new Date(alert.checked_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getRiskColor(alert.risk_level)}>
                          Risk: {alert.risk_score}
                        </Badge>
                      </div>
                    </div>

                    <div className="bg-stone-50 rounded-lg p-3 mb-4">
                      <p className="text-sm font-medium text-stone-700 mb-2">Risk Factors:</p>
                      <ul className="text-sm text-stone-600 space-y-1">
                        {alert.risk_factors?.map((factor, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-yellow-600" />
                            {factor}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {reviewingId === alert.id ? (
                      <div className="space-y-3">
                        <Label>Review Notes</Label>
                        <Textarea
                          value={reviewNotes}
                          onChange={(e) => setReviewNotes(e.target.value)}
                          placeholder="Add notes about your decision..."
                          rows={2}
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleReview(alert.id, true)}
                            className="bg-green-600 hover:bg-green-700"
                            disabled={submitting}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Approve Order
                          </Button>
                          <Button
                            onClick={() => handleReview(alert.id, false)}
                            variant="destructive"
                            disabled={submitting}
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Block Order
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => setReviewingId(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        onClick={() => setReviewingId(alert.id)}
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Review
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="logs" className="space-y-4">
            {logs.map((log) => (
              <Card key={log.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-stone-800">{log.email}</p>
                      <p className="text-sm text-stone-500">
                        {new Date(log.checked_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getRiskColor(log.risk_level)}>
                        {log.risk_level} ({log.risk_score})
                      </Badge>
                      {log.reviewed && (
                        <Badge className={log.review_decision === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {log.review_decision}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="flagged" className="space-y-4">
            {flaggedEmails.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Mail className="h-16 w-16 mx-auto mb-4 text-stone-300" />
                  <h3 className="text-lg font-medium text-stone-800 mb-2">No flagged emails</h3>
                </CardContent>
              </Card>
            ) : (
              flaggedEmails.map((flag) => (
                <Card key={flag.id}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-stone-800">{flag.email}</p>
                      <p className="text-sm text-stone-500">{flag.reason}</p>
                      <p className="text-xs text-stone-400">
                        Flagged: {new Date(flag.flagged_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUnflagEmail(flag.email)}
                    >
                      Unflag
                    </Button>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminFraud;
